import { apiFetch } from '../config/api'

import type { PipeSizeSet, ProjectDetails } from '../types'



export interface Data360ProjectSummary {

  projectId: number

  projectName: string

  projectNumber: string

  customer?: string

  location?: string

}



export interface HubProject extends ProjectDetails {

  hubKind?: 'legacy' | 'data360-open' | 'data360-new'

  wpsShadowId?: string | null

}



export interface Data360ProductionWeldOption {

  productionWeldId: number

  productionId: number

  assemblyNumber: string

  assemblyLabel: string

  weldName: string

  deviceWeldId?: string

  jobNumber?: string

}



export interface Data360SyncedPipeSet {

  id: string

  data360PipeId: number

  nominalOd: string

  nominalOdUnit: string

  wallThickness: string

  wallThicknessUnit: string

  pipeName?: string

  data360ProjectCategory?: 'pipeline' | 'fabrication'

  data360ProjectSubType?: string

}



export interface WpsProjectCreatePayload {

  project: {

    name: string

    projectNumber: string

    location?: string

    contractors: string[]

    endClients: string[]

    applications: string[]

    weldingCodes: string[]

    clientWeldingSpecs: string[]

    equipment: string[]

    dueDate?: string

    pipeSizeRange?: string

    targetWpsCount?: number

    status?: string

  }

  pipes: Array<{

    id: string

    nominalOd: string

    nominalOdUnit: string

    wallThickness: string

    wallThicknessUnit: string

    pipeName?: string

    source?: string

    nominalThicknessRangeMin?: string

    nominalThicknessRangeMax?: string

    bevelType?: string

    backingRequirement?: string

    backingMaterial?: string

    materials?: Array<{

      materialSpec: string

      typeGrade: string

      manufacture: string

      source: string

    }>

  }>

}



export interface HubProjectsResponse {
  projects: HubProject[]
  warnings: string[]
}

export function fetchHubProjects(): Promise<HubProjectsResponse> {

  return apiFetch('/api/data360/hub-projects')

}



export function fetchData360Projects(): Promise<Data360ProjectSummary[]> {

  return apiFetch('/api/data360/projects')

}



export function createData360Project(
  payload: WpsProjectCreatePayload
): Promise<{ shadowProjectId: string; data360ProjectId: number; project?: ProjectDetails }> {
  return apiFetch('/api/data360/projects', {
    method: 'POST',
    body: JSON.stringify(payload)
  })
}



export function ensureData360Workspace(

  data360ProjectId: number

): Promise<{ shadowProjectId: string; project?: ProjectDetails; pipeSets: Data360SyncedPipeSet[] }> {

  return apiFetch(`/api/data360/projects/${data360ProjectId}/ensure-workspace`, {

    method: 'POST',

    body: JSON.stringify({})

  })

}



export function updateData360ExtraDetails(

  data360ProjectId: number,

  body: Partial<WpsProjectCreatePayload['project']>

): Promise<{ ok: boolean }> {

  return apiFetch(`/api/data360/projects/${data360ProjectId}/extra-details`, {

    method: 'PUT',

    body: JSON.stringify(body)

  })

}



export function fetchWpsProjectPipeSets(wpsProjectId: string): Promise<Data360SyncedPipeSet[]> {

  return apiFetch(`/api/data360/wps-projects/${wpsProjectId}/pipe-sets`)

}



export function fetchProductionWelds(

  wpsProjectId: string,

  wpsPipeSetId: string

): Promise<Data360ProductionWeldOption[]> {

  return apiFetch(`/api/data360/wps-projects/${wpsProjectId}/pipe-sets/${wpsPipeSetId}/production-welds`)

}



export function fetchWeldImportStatus(

  wpsProjectId: string,

  wpsPipeSetId: string,

  weldRowId: string

): Promise<{ hasData: boolean }> {

  return apiFetch(

    `/api/data360/wps-projects/${wpsProjectId}/pipe-sets/${wpsPipeSetId}/welds/${weldRowId}/import-status`

  )

}



export interface ImportWeldFromData360Result {
  replaced: boolean
  sampleCount: number
  passCount: number
  distinctPassNames: string[]
  passMappingComplete: boolean
  uploadedAt: string
  sourceFilename: string
  passMappings: Record<string, string>
  sectionsVersion: number
}

export function importWeldFromData360(

  wpsProjectId: string,

  wpsPipeSetId: string,

  weldRowId: string,

  body: { productionWeldId: number; weldName: string; jobNumber?: string }

): Promise<ImportWeldFromData360Result> {

  return apiFetch(

    `/api/data360/wps-projects/${wpsProjectId}/pipe-sets/${wpsPipeSetId}/welds/${weldRowId}/import-from-data360`,

    {

      method: 'POST',

      body: JSON.stringify(body)

    }

  )

}



export function fetchProductionWeldSnapshot(

  wpsProjectId: string,

  wpsPipeSetId: string,

  productionWeldId: number

): Promise<Record<string, unknown>> {

  const qs = new URLSearchParams({ wpsPipeSetId })

  return apiFetch(

    `/api/data360/wps-projects/${wpsProjectId}/production-welds/${productionWeldId}/snapshot?${qs}`

  )

}



export function hubProjectToDetails(project: HubProject): ProjectDetails {

  const id = project.wpsShadowId || project.id

  return {

    ...project,

    id,

    pipeSets: (project.pipeSets ?? []) as PipeSizeSet[]

  }

}



export const PHANTOM_PROJECT_PREFIX = 'd360-shadow-'

export function hubOpenPath(project: HubProject): string {
  if (project.hubKind === 'legacy') {
    return `/projects/${project.id}/next`
  }

  if (project.wpsShadowId) {
    return `/projects/${project.wpsShadowId}/next`
  }

  const data360Id = Number(project.data360ProjectId)
  if (!data360Id) {
    throw new Error('Missing Data360 project id')
  }

  return `/projects/${PHANTOM_PROJECT_PREFIX}${data360Id}/next`
}


